import { useSyncExternalStore, useState } from 'react';
import { assetsChaptersBulkAddUpdateCreate, ChapterResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';
import { ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import {
  ClipDeletes,
  ClipEdits
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { SourceType } from '@goldcast/player-config/src/types/asset-types';
import { Scenes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import useAnalytics from '@/hooks/useAnalytics';
import useFreeTrialHook from '@/hooks/useFreeTrialHook';
import { createContentClip } from '@/libs/clipContentUtil';
import { showErrorToast } from '@/libs/toast/toast';
import { prependAllClips, setClipGeneratingCount } from '@/stores/clip';
import { core } from '@/stores/core';
import { addUnseenClipIds } from '@/stores/unseenClips';
import { appendRecordingDuplicate } from '@/stores/duplicateRecordingStore';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { useAppContext } from '@/context/AppContext/AppContext';
import { addPendingUpdate, PendingEditType } from '@/stores/pendingEditsStore';
import { appendChapterHistory } from '@/stores/clipHistory';
import { clipVersionStore } from '@/stores/clipVersionStore';
import { VERSION_EDIT_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';

export default function useTranscriptPage() {
  const [selectedChapter, setSelectedChapter] = useState<ChapterResponse | null>(null);

  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const transcriptStore = useTranscriptContext();
  const { clipId: clipIdFromContext } = useClipsContext();

  const { incrementClipsUsed } = useFreeTrialHook();
  const { trackContentCreated } = useAnalytics();
  const { logger } = useAppContext();

  const sortChaptersByStartTime = (chapters: ChapterResponse[]) => {
    return [...chapters].sort((a, b) => a.start_time - b.start_time);
  };

  const getClipIdFromPath = (): string | null => {
    const currentPath = window.location?.pathname;
    if (!currentPath) {
      return null;
    }
    const match = currentPath.match(/\/clips\/([^/?#]+)/);
    return match?.[1] || null;
  };

  const getResolvedClipId = (): string | null => {
    return clipIdFromContext || getClipIdFromPath();
  };

  const trackChapterSyncPromise = <T>(updatePromise: Promise<T>): Promise<T> => {
    const clipId = getResolvedClipId();
    if (!clipId) {
      return updatePromise;
    }
    addPendingUpdate({ clipId, updatePromise, version: Date.now(), editType: PendingEditType.chapter });
    return updatePromise;
  };

  async function saveClip(
    {
      start,
      end,
      deletes,
      edits,
      scenes
    }: Pick<ClipMetadata, 'start' | 'end' | 'deletes' | 'edits'> & {
      scenes?: Scenes;
    },
    source: SourceType,
    fps?: number,
    additionalParams?: Record<string, any>,
    isDuplicate?: boolean,
    title?: string,
    isTimelineSelection = false
  ) {
    try {
      const newClip = await createContentClip(
        start,
        end,
        source,
        deletes,
        edits,
        scenes,
        fps,
        additionalParams,
        isDuplicate,
        title
      );
      if (!isDuplicate) {
        prependAllClips([{ ...newClip, isRemoved: false } as any]);
        addUnseenClipIds([newClip.id]);
        if (!coreStore.content?.is_sample_upload) {
          incrementClipsUsed(1);
        }
      } else {
        const broadcastId = coreStore.content?.id;
        if (broadcastId) {
          appendRecordingDuplicate(broadcastId, newClip as any);
        }
      }
      trackContentCreated({
        asset: newClip,
        state: 'Created',
        source: 'Manual',
        isDuplicateRecording: isDuplicate,
        isTimelineSelection
      });
      return newClip;
    } catch (err: any) {
      trackContentCreated({
        source: 'Manual',
        state: 'Failed',
        failureReason: err,
        isDuplicateRecording: isDuplicate,
        isTimelineSelection
      });

      showErrorToast("Creating the clip didn't quite work out. Double-check the time range and give it another shot.");
    }
  }

  const createClip = (
    start,
    end,
    {
      edits,
      deletes,
      scenes,
      fps,
      additionalParams,
      isDuplicate,
      title,
      isTimelineSelection
    }: {
      edits: ClipEdits;
      deletes: ClipDeletes;
      scenes?: Scenes;
      fps?: number;
      additionalParams?: Record<string, any>;
      isDuplicate?: boolean;
      title?: string;
      isTimelineSelection?: boolean;
    } = {
      edits: {},
      deletes: {}
    }
  ) => {
    setClipGeneratingCount(1);
    return saveClip(
      {
        start,
        end,
        edits,
        deletes,
        scenes
      },
      'USER',
      fps,
      additionalParams,
      isDuplicate,
      title,
      isTimelineSelection
    ).then(newClip => {
      setClipGeneratingCount(0);
      return newClip;
    });
  };

  const updateChapter = (chapterId: string, title?: string, startTime?: number) => {
    if (!coreStore.content?.id) {
      return Promise.resolve();
    }

    const previousChapters = (transcriptStore?.chapterData || []).map(chapter => ({ ...chapter }));
    if (!previousChapters.some(chapter => chapter.id === chapterId)) {
      return Promise.resolve();
    }

    const chaptersWithUpdates =
      previousChapters?.map(chapter =>
        chapter.id === chapterId
          ? {
              ...chapter,
              ...(title !== undefined && { title }),
              ...(startTime !== undefined && { start_time: startTime })
            }
          : chapter
      ) || [];
    const updatedChapters = previousChapters ? sortChaptersByStartTime(chaptersWithUpdates) : undefined;

    if (updatedChapters) {
      EventBus.dispatch(CustomEvents.UpdateChapters, {
        chapterData: updatedChapters,
        chaptersLastUpdatedAt: new Date().toISOString()
      });
      const clipId = getResolvedClipId();
      if (clipId) {
        appendChapterHistory(clipId, updatedChapters);
        clipVersionStore.emitEdit(clipId, VERSION_EDIT_TYPE.CHAPTER_UPDATE);
      }
    }

    const chapterUpdatePromise = assetsChaptersBulkAddUpdateCreate({
      body: updatedChapters || [],
      queryParams: {
        content_id: coreStore.content?.id
      }
    });

    return trackChapterSyncPromise(chapterUpdatePromise).catch(err => {
      EventBus.dispatch(CustomEvents.UpdateChapters, {
        chapterData: previousChapters,
        chaptersLastUpdatedAt: new Date().toISOString()
      });
      showErrorToast('Failed to update chapter. Please try again in a few minutes.');
      logger.error('Failed to update chapter', err);
    });
  };

  const createNewChapter = async (title: string, chapterStartTime: number) => {
    if (!coreStore.content?.id) {
      return;
    }

    const previousChapters = transcriptStore?.chapterData;
    const newChapterId = uuidv4();
    const optimisticChapter: ChapterResponse = {
      id: newChapterId,
      start_time: chapterStartTime,
      title,
      is_active: true
    };
    const optimisticChapters = previousChapters
      ? sortChaptersByStartTime([...previousChapters, optimisticChapter])
      : [];

    if (optimisticChapters) {
      EventBus.dispatch(CustomEvents.UpdateChapters, {
        chapterData: optimisticChapters,
        chaptersLastUpdatedAt: new Date().toISOString()
      });
      const clipId = getResolvedClipId();
      if (clipId) {
        appendChapterHistory(clipId, optimisticChapters);
        clipVersionStore.emitEdit(clipId, VERSION_EDIT_TYPE.CHAPTER_ADD);
      }
    }

    const createChapterPromise = assetsChaptersBulkAddUpdateCreate({
      body: optimisticChapters,
      queryParams: {
        content_id: coreStore.content?.id
      }
    });

    return trackChapterSyncPromise(createChapterPromise).catch(err => {
      logger.error('Failed to create chapter', err);
    });
  };

  return {
    createClip,
    saveClip,
    updateChapter,
    createNewChapter,
    selectedChapter,
    setSelectedChapter,
    chapters: transcriptStore.chapterData,
    isAudio: coreStore.content?.media_type === 'AUDIO'
  };
}
